import { Activity, ArrowLeft, Binary, BrainCircuit, CheckCircle2, Database, Radio, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CASE_001, characterById } from '../case/case001';
import { verifyYuragiAgent, yuragiEventCheckpoint } from '../engine/courtAgents';
import { shortHash } from '../engine/yuragi';
import { useGame } from '../game/GameContext';
import type { CharacterId } from '../game/types';

export function DossierOverlay() {
  const { state, dispatch } = useGame();
  const [selected, setSelected] = useState<CharacterId>('lyra');
  const character = characterById[selected];
  const agent = state.yuragi.agents[selected];
  const replayVerified = useMemo(
    () => verifyYuragiAgent(state.yuragi, selected),
    [selected, state.yuragi],
  );
  const facts = Object.values(agent.knowledge)
    .sort((a, b) => b.baseConfidence - a.baseConfidence)
    .slice(0, 3);
  return (
    <div className="overlay-backdrop screen-layer">
      <section className="dossier panel-surface">
        <header className="panel-header">
          <button className="icon-action" onClick={() => dispatch({ type: 'CLOSE_OVERLAY' })}><ArrowLeft /></button>
          <div><span>TRIBUNAL INDEX</span><strong>Persons of interest</strong></div>
          <button className="icon-action" onClick={() => dispatch({ type: 'CLOSE_OVERLAY' })}><X /></button>
        </header>
        <div className="dossier__rail">
          {CASE_001.characters.map((item) => (
            <button key={item.id} className={item.id === selected ? 'is-active' : ''} onClick={() => setSelected(item.id)}>
              <img src={item.portrait} alt="" /><span>{item.name}</span><small>{item.shortRole}</small>
            </button>
          ))}
        </div>
        <article className="dossier__profile">
          <div className="dossier__image"><img src={character.portrait} alt={character.name} /><span>{shortHash(agent.stateHash)}</span></div>
          <div className="dossier__copy">
            <p className="eyebrow">{character.shortRole}</p><h2>{character.name}</h2><h3>{character.role}</h3>
            <p>{character.summary}</p>
            <blockquote>{character.tell}</blockquote>
            <div className="agent-runtime-card">
              <span><BrainCircuit /> LIVE AGENT / BLOCK {state.yuragi.height}</span>
              <strong>{agent.decision}</strong>
              <p>{agent.stance} · arousal {Math.round(agent.arousal * 100)} · trust {Math.round(agent.trust * 100)}</p>
              <div><i style={{ width: `${Math.round(agent.arousal * 100)}%` }} /></div>
            </div>
            <div className="temperament">
              <span><Activity /> LIVE YURAGI TEMPERAMENT</span>
              {Object.entries(agent.personality).map(([key, value]) => (
                <div key={key}><label>{key}</label><i><b style={{ width: `${Math.round(value * 100)}%` }} /></i><strong>{Math.round(value * 100)}</strong></div>
              ))}
            </div>
            <div className="agent-memory">
              <span><Database /> HASH-CHAINED EXPERIENCE · {agent.eventSeq} EVENTS</span>
              {facts.length > 0 ? facts.map((fact) => (
                <p key={fact.key}><strong>{fact.subject}</strong><span>{fact.predicate}</span><small>{Math.round(fact.baseConfidence * 100)}% base confidence</small></p>
              )) : <p><span>No learned facts yet. Question or confront this agent.</span></p>}
            </div>
            <div className="agent-proof-grid">
              <div><Radio /><span>COUPLED STATE</span><strong>{Math.round(state.yuragi.syncOrder * 100)}% sync · K {state.yuragi.couplingK.toFixed(2)}</strong></div>
              <div><CheckCircle2 /><span>GENESIS REPLAY</span><strong>{replayVerified ? 'VERIFIED EXACT' : 'PROOF MISMATCH'}</strong></div>
              <div><Binary /><span>DETERMINISTIC MOTIF</span><strong>{agent.motif.rootName} · {agent.motif.tempo} BPM</strong></div>
            </div>
            <div className="agent-artifact"><span>DERIVED PURSUIT / {agent.artifact.theme}</span><strong>{agent.artifact.title}</strong><p>{agent.artifact.objective}</p><small>{shortHash(agent.artifact.hash)}</small></div>
            <div className="state-signature"><Binary /><span>Mind-aware state signature</span><strong>{agent.stateHash.toUpperCase()}</strong></div>
            <div className="event-checkpoint"><span>EVENT CHECKPOINT</span><code>{yuragiEventCheckpoint(agent)}</code></div>
          </div>
        </article>
      </section>
    </div>
  );
}
